import { useState } from 'react';
import { 
  Printer, 
  Scissors, 
  Bot, 
  Wrench, 
  GraduationCap, 
  Code, 
  Palette, 
  Coffee,
  Clock,
  Users,
  Star
} from 'lucide-react';

const Services = () => {
  const [activeTab, setActiveTab] = useState('fablab');

  const fablabServices = [
    {
      icon: <Printer className="h-8 w-8" />,
      title: "Impression 3D Haute Précision",
      description: "Impression haute précision pour prototypage rapide et production de pièces complexes.",
      features: ["Technologies: FDM, SLA, SLS", "Divers matériaux", "Post-traitement"]
    },
    {
      icon: <Scissors className="h-8 w-8" />,
      title: "Découpe Laser & Gravure",
      description: "Découpe et gravure de précision sur multiples matériaux.",
      features: ["Bois, Plexiglass, Alucobond, PVC, etc.", "Gravure personnalisée", "Prototypage rapide"]
    },
    {
      icon: <Bot className="h-8 w-8" />,
      title: "Robotique & Automatisation",
      description: "Conception et programmation de systèmes automatisés, IoT et solutions embarquées.",
      features: ["Arduino, Raspberry Pi, ESP, Micro:bit, etc.", "Composants et dispositifs électroniques", "Systèmes embarqués et développement PCB"]
    },
    {
      icon: <Wrench className="h-8 w-8" />,
      title: "Support de Projet",
      description: "Support expert de l'idée à la réalisation avec mentorat personnalisé.",
      features: ["Consultation technique", "Suivi de projet", "Validation de prototype"]
    }
  ];

  const formationServices = [
    {
      icon: <GraduationCap className="h-8 w-8" />,
      title: "Formations Certifiantes",
      description: "Programmes structurés de formation aux technologies numériques et fabrication",
      features: ["Niveaux débutant à expert", "Certification officielle", "Suivi personnalisé"]
    },
    {
      icon: <Code className="h-8 w-8" />,
      title: "Bootcamps Intensifs",
      description: "Formations accélérées pour acquérir rapidement des compétences opérationnelles",
      features: ["1-4 semaines", "Projets concrets", "Petits groupes"]
    },
    {
      icon: <Palette className="h-8 w-8" />,
      title: "Clubs Techno-Artistiques",
      description: "Espaces d'expression créative mêlant art, design et technologie",
      features: ["Art numérique", "Design interactif", "Installations créatives"]
    },
    {
      icon: <Coffee className="h-8 w-8" />,
      title: "Coworking & Étude",
      description: "Espaces collaboratifs équipés pour le travail, l'étude et la création",
      features: ["WiFi haut débit", "Espaces modulables", "Communauté active"]
    }
  ];

  const currentServices = activeTab === 'fablab' ? fablabServices : formationServices;

  return (
    <section id="services" className="py-20 bg-gray-50 dark:bg-gray-900 relative">
      <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.02)_1px,transparent_1px)] dark:bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:100px_100px]"></div>
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">Nos Services</h2>
          <p className="text-xl text-gray-700 dark:text-white/70 mb-4">Découvrez notre gamme complète de services pour concrétiser vos projets les plus ambitieux</p>
          <p className="text-gray-600 dark:text-white/70 leading-relaxed">
            Une offre complète pour transformer vos idées en réalité, 
            développer vos compétences et rejoindre une communauté d'innovateurs.
          </p>
        </div>

        <div className="flex justify-center mb-12">
          <div className="inline-flex bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-full p-1 border border-gray-200 dark:border-white/10 shadow-sm">
            <button
              onClick={() => setActiveTab('fablab')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 ${activeTab === 'fablab' ? 'bg-white dark:bg-white/90 text-gray-900 shadow-sm' : 'text-gray-700 dark:text-white/70 hover:text-gray-900 dark:hover:text-white'}`}
            >
              FabLab
            </button>
            <button
              onClick={() => setActiveTab('formation')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 ${activeTab === 'formation' ? 'bg-white dark:bg-white/90 text-gray-900 shadow-sm' : 'text-gray-700 dark:text-white/70 hover:text-gray-900 dark:hover:text-white'}`}
            >
              Formations
            </button>

          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {currentServices.map((service, index) => {
            const IconComponent = service.icon.type;
            return (
              <div 
                key={index}
                className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-gray-200 dark:border-white/10 hover:border-gray-300 dark:hover:border-white/20 transition-all duration-300 h-full shadow-sm hover:shadow-md hover:-translate-y-1"
              >
                <div className="w-12 h-12 bg-primary-100 dark:bg-white/10 rounded-lg flex items-center justify-center mb-4">
                  <IconComponent className="h-6 w-6 text-primary-600 dark:text-white" />
                </div>
                <h3 className="text-gray-900 dark:text-white font-semibold text-lg mb-2">{service.title}</h3>
                <p className="text-gray-600 dark:text-white/60 text-sm mb-4">{service.description}</p>
                <div className="space-y-2">
                  {service.features.map((feature, i) => (
                    <div key={i} className="flex items-center text-gray-600 dark:text-white/60 text-sm">
                      <Star className="h-3 w-3 mr-2 text-primary-400" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="bg-white/90 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-8 md:p-12 border border-gray-200 dark:border-white/10 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div className="flex flex-col items-center">
              <Clock className="h-12 w-12 text-primary-600 dark:text-white mb-4 group-hover:rotate-12 transition-transform duration-300" />
              <h4 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Horaires Flexibles</h4>
              <p className="text-gray-600 dark:text-white/70">Accès 24/7 pour les membres</p>
            </div>
            <div className="flex flex-col items-center">
              <Users className="h-12 w-12 text-primary-600 dark:text-white mb-4 group-hover:rotate-12 transition-transform duration-300" />
              <h4 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Communauté Active</h4>
              <p className="text-gray-600 dark:text-white/70">Rencontrez d'autres créateurs</p>
            </div>
            <div className="flex flex-col items-center">
              <Star className="h-12 w-12 text-primary-600 dark:text-white mb-4 group-hover:rotate-12 transition-transform duration-300" />
              <h4 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Équipement Pro</h4>
              <p className="text-gray-600 dark:text-white/70">Matériel de qualité professionnelle</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;